/**
 * Canonical response-level coverage for multi-provider tools.
 *
 * Only `scored` providers contribute to the top-level denominator. Rollout
 * providers such as Grok remain visible in `providers`, but their responses
 * can never make a scored run complete or degraded.
 */

export type ResponseCoverageRole = 'scored' | 'report_only'

export type ProviderCoverageStatus = 'complete' | 'partial' | 'unavailable'

export interface ProviderResponseCoverage {
  provider: string
  role: ResponseCoverageRole
  status: ProviderCoverageStatus
  expectedResponses: number
  usableResponses: number
  failedResponses: number
  emptyResponses: number
  /** Historical results may know a response was missing but not why. */
  unknownResponses: number
  unattemptedResponses: number
}

export interface ResponseCoverage {
  schemaVersion: 1
  status: 'complete' | 'degraded'
  expectedResponses: number
  usableResponses: number
  failedResponses: number
  emptyResponses: number
  unknownResponses: number
  unattemptedResponses: number
  completenessPercent: number
  providers: ProviderResponseCoverage[]
}

export interface ProviderResponseCoverageInput {
  provider: string
  role?: ResponseCoverageRole
  expectedResponses: number
  usableResponses: number
  failedResponses?: number
  emptyResponses?: number
  unknownResponses?: number
}

function responseCount(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`${field} must be a non-negative integer`)
  }
  return value
}

/**
 * Build and validate one response-coverage object. Invalid accounting throws:
 * silently normalising impossible counts would make the diagnostic contract
 * look trustworthy while hiding a producer bug.
 */
export function buildResponseCoverage(
  inputs: ProviderResponseCoverageInput[]
): ResponseCoverage {
  const providers = inputs.map(input => {
    const expectedResponses = responseCount(
      input.expectedResponses,
      `${input.provider}.expectedResponses`
    )
    const usableResponses = responseCount(
      input.usableResponses,
      `${input.provider}.usableResponses`
    )
    const failedResponses = responseCount(
      input.failedResponses ?? 0,
      `${input.provider}.failedResponses`
    )
    const emptyResponses = responseCount(
      input.emptyResponses ?? 0,
      `${input.provider}.emptyResponses`
    )
    const unknownResponses = responseCount(
      input.unknownResponses ?? 0,
      `${input.provider}.unknownResponses`
    )
    const accounted =
      usableResponses + failedResponses + emptyResponses + unknownResponses

    if (accounted > expectedResponses) {
      throw new Error(
        `${input.provider} accounts for ${accounted} of ${expectedResponses} expected responses`
      )
    }

    const unattemptedResponses = expectedResponses - accounted
    const status: ProviderCoverageStatus =
      usableResponses === expectedResponses
        ? 'complete'
        : usableResponses === 0
          ? 'unavailable'
          : 'partial'

    return {
      provider: input.provider,
      role: input.role ?? 'scored',
      status,
      expectedResponses,
      usableResponses,
      failedResponses,
      emptyResponses,
      unknownResponses,
      unattemptedResponses,
    }
  })

  const scored = providers.filter(provider => provider.role === 'scored')
  const sum = (field: keyof ProviderResponseCoverage) =>
    scored.reduce((total, provider) => {
      const value = provider[field]
      return total + (typeof value === 'number' ? value : 0)
    }, 0)

  const expectedResponses = sum('expectedResponses')
  const usableResponses = sum('usableResponses')
  const failedResponses = sum('failedResponses')
  const emptyResponses = sum('emptyResponses')
  const unknownResponses = sum('unknownResponses')
  const unattemptedResponses = sum('unattemptedResponses')
  const status =
    expectedResponses > 0 && usableResponses === expectedResponses
      ? 'complete'
      : 'degraded'

  return {
    schemaVersion: 1,
    status,
    expectedResponses,
    usableResponses,
    failedResponses,
    emptyResponses,
    unknownResponses,
    unattemptedResponses,
    completenessPercent:
      expectedResponses === 0
        ? 0
        : Math.round((usableResponses / expectedResponses) * 100),
    providers,
  }
}

interface LegacyProviderCoverage {
  provider?: unknown
  expectedQueryCount?: unknown
  usableQueryCount?: unknown
  reportOnly?: unknown
}

interface CoverageResultLike {
  coverage?: unknown
  providers?: unknown
  expectedResponseCount?: unknown
  usableResponseCount?: unknown
}

/**
 * Read canonical coverage or reconstruct only what a historical saved result
 * actually proves. Historical gaps are `unknownResponses`, never guessed as a
 * provider failure, blank response, or unattempted request.
 */
export function responseCoverageForResult(
  result: CoverageResultLike
): ResponseCoverage | null {
  if (
    result.coverage &&
    typeof result.coverage === 'object' &&
    (result.coverage as ResponseCoverage).schemaVersion === 1
  ) {
    return result.coverage as ResponseCoverage
  }

  if (Array.isArray(result.providers)) {
    const legacyProviders = result.providers as LegacyProviderCoverage[]
    if (
      legacyProviders.length > 0 &&
      legacyProviders.every(
        provider =>
          typeof provider.provider === 'string' &&
          typeof provider.expectedQueryCount === 'number' &&
          Number.isInteger(provider.expectedQueryCount) &&
          provider.expectedQueryCount >= 0 &&
          typeof provider.usableQueryCount === 'number' &&
          Number.isInteger(provider.usableQueryCount) &&
          provider.usableQueryCount >= 0 &&
          provider.usableQueryCount <= provider.expectedQueryCount
      )
    ) {
      return buildResponseCoverage(
        legacyProviders.map(provider => {
          const expectedResponses = provider.expectedQueryCount as number
          const usableResponses = provider.usableQueryCount as number
          return {
            provider: provider.provider as string,
            role:
              provider.reportOnly === true
                ? ('report_only' as const)
                : ('scored' as const),
            expectedResponses,
            usableResponses,
            unknownResponses: expectedResponses - usableResponses,
          }
        })
      )
    }
  }

  if (
    typeof result.expectedResponseCount === 'number' &&
    typeof result.usableResponseCount === 'number' &&
    Number.isInteger(result.expectedResponseCount) &&
    Number.isInteger(result.usableResponseCount) &&
    result.expectedResponseCount >= result.usableResponseCount &&
    result.usableResponseCount >= 0
  ) {
    const expectedResponses = result.expectedResponseCount
    const usableResponses = result.usableResponseCount
    const aggregate = buildResponseCoverage([
      {
        provider: 'Historical aggregate',
        expectedResponses,
        usableResponses,
        unknownResponses: expectedResponses - usableResponses,
      },
    ])
    return { ...aggregate, providers: [] }
  }

  return null
}
